import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Users, Ticket, Layers, Mail, Send, ChevronLeft, ChevronRight, AlertTriangle, Check, Sparkles, Clock, CalendarClock, Eye } from 'lucide-react';
import emailCampaignsApi from 'api/requests/emailCampaigns';
import Button from 'components/_UI/Button';
import Checkbox from 'components/_UI/Checkbox';
import Loader from 'components/_UI/Loader';
import Radio from 'components/_UI/Radio';
import Text from 'components/_UI/Text';
import RichTextEditor from 'components/commons/RichText';
import EmailPreviewFrame from './EmailPreviewFrame';
import { FullyCentered } from 'styled/alignment/Center';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const ACCENT_COLOR = panelAccent(colors.secondaryPurple);
const ACCENT_SOFT = panelAccentAlpha(colors.secondaryPurple, '12');

const AUDIENCE_OPTIONS = [
    { name: 'customers', label: 'Todos os clientes', hint: 'Todo mundo que já comprou de você', icon: Users },
    { name: 'event_participants', label: 'Participantes de um evento', hint: 'Só quem comprou pra um evento específico', icon: Ticket },
    { name: 'event_combination', label: 'Combinação de eventos', hint: 'Junta o público de 2 ou mais eventos', icon: Layers },
];

const MIN_COMBINATION_EVENTS = 2;

const CUSTOMER_VARIABLES = [
    { key: 'nome', label: 'Nome' },
];

const EVENT_VARIABLES = [
    { key: 'nome', label: 'Nome' },
    { key: 'evento', label: 'Evento' },
    { key: 'ingresso', label: 'Ingresso' },
    { key: 'produto', label: 'Produto' },
    { key: 'codigo', label: 'Código' },
];

// Variáveis disponíveis dependem do público: numa campanha de "todos os
// clientes" não existe um evento/ingresso único por destinatário (a pessoa
// pode ter comprado de vários), então esses campos não fazem sentido ali —
// e ficariam em branco no e-mail se usados (ver SendCampaignEmailJob).
const VARIABLES_BY_AUDIENCE = {
    customers: CUSTOMER_VARIABLES,
    event_participants: EVENT_VARIABLES,
    event_combination: EVENT_VARIABLES,
};

const STEPS = [
    { key: 'audience', label: 'Público', icon: Users },
    { key: 'content', label: 'Conteúdo', icon: Mail },
    { key: 'review', label: 'Revisão e envio', icon: Send },
];

/* Sem `err.response`, a requisição nem chegou a um servidor — mesma
   distinção usada no resto da tela de clientes (getErrorMessage). */
function getErrorMessage(err, fallback) {
    if (!err?.response) {
        return 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
    }
    return err?.response?.data?.message || fallback;
}

export default function EmailCampaignComposer({ events = [], onCreated = () => {} }) {
    const [step, setStep] = useState(1);

    const [audienceType, setAudienceType] = useState('customers');
    const [eventId, setEventId] = useState('');
    const [selectedEventIds, setSelectedEventIds] = useState([]);
    const [audienceCount, setAudienceCount] = useState(null);
    const [audienceCountLoading, setAudienceCountLoading] = useState(false);
    const [audienceCountError, setAudienceCountError] = useState(null);

    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [bodyText, setBodyText] = useState('');

    const [sendMode, setSendMode] = useState('now');
    const [scheduledAt, setScheduledAt] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const subjectRef = useRef(null);
    const bodyEditorRef = useRef(null);
    const availableVariables = VARIABLES_BY_AUDIENCE[audienceType] || [];

    function insertVariableIntoSubject(token) {
        const el = subjectRef.current;
        const start = el?.selectionStart ?? subject.length;
        const end = el?.selectionEnd ?? subject.length;
        const next = subject.slice(0, start) + token + subject.slice(end);
        setSubject(next);

        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            el.setSelectionRange(start + token.length, start + token.length);
        });
    }

    // Contagem de destinatários é recalculada a cada troca de público/evento(s),
    // pra o produtor saber pra quantas pessoas vai antes de sair da etapa 1.
    useEffect(() => {
        if (audienceType === 'event_participants' && !eventId) {
            setAudienceCount(null);
            setAudienceCountError(null);
            return;
        }

        if (audienceType === 'event_combination' && selectedEventIds.length < MIN_COMBINATION_EVENTS) {
            setAudienceCount(null);
            setAudienceCountError(null);
            return;
        }

        let active = true;
        setAudienceCountLoading(true);
        setAudienceCountError(null);

        emailCampaignsApi.getAudienceCount({
            audience_type: audienceType,
            event_id: audienceType === 'event_participants' ? eventId : undefined,
            event_ids: audienceType === 'event_combination' ? selectedEventIds : undefined,
        }).then(({ data }) => {
            if (!active) return;
            setAudienceCount(data?.data?.count ?? 0);
        }).catch((err) => {
            if (!active) return;
            setAudienceCount(null);
            setAudienceCountError(getErrorMessage(err, 'Não foi possível calcular o público.'));
        }).finally(() => {
            if (active) setAudienceCountLoading(false);
        });

        return () => { active = false; };
    }, [audienceType, eventId, selectedEventIds]);

    const canAdvanceFromAudience = !audienceCountLoading && !audienceCountError && (audienceCount || 0) > 0
        && (audienceType !== 'event_combination' || selectedEventIds.length >= MIN_COMBINATION_EVENTS);
    const canAdvanceFromContent = subject.trim().length > 0 && bodyText.trim().length > 0;

    // Mínimo 5 minutos no futuro, pra dar tempo do agendamento não cair "no
    // passado" só pelo tempo que o produtor leva pra confirmar o formulário.
    const minScheduledAt = new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16);
    const canSubmit = sendMode === 'now' || (!!scheduledAt && scheduledAt >= minScheduledAt);

    function toggleCombinationEvent(id) {
        setSelectedEventIds(current => current.includes(id)
            ? current.filter(existing => existing !== id)
            : [...current, id]);
    }

    function goNext() {
        setStep(current => Math.min(current + 1, STEPS.length));
    }

    function goBack() {
        setSubmitError(null);
        setStep(current => Math.max(current - 1, 1));
    }

    async function handleSubmit() {
        setSubmitting(true);
        setSubmitError(null);
        try {
            await emailCampaignsApi.create({
                subject,
                body,
                audience_type: audienceType,
                event_id: audienceType === 'event_participants' ? eventId : undefined,
                event_ids: audienceType === 'event_combination' ? selectedEventIds : undefined,
                scheduled_at: sendMode === 'schedule' ? new Date(scheduledAt).toISOString() : undefined,
            });
            onCreated({ scheduled: sendMode === 'schedule' });
        } catch (err) {
            setSubmitError(getErrorMessage(err, 'Ocorreu um erro ao criar a campanha.'));
        } finally {
            setSubmitting(false);
        }
    }

    const selectedEvent = events.find(ev => String(ev.id) === String(eventId));
    const selectedCombinationEvents = events.filter(ev => selectedEventIds.includes(ev.id));

    return (
        <Wrapper>
            <ComposerHeader>
                <ComposerHeaderText>
                    <ComposerIcon><Mail size={16} strokeWidth={2.2} /></ComposerIcon>
                    <Text type='bold'>Nova campanha</Text>
                </ComposerHeaderText>
            </ComposerHeader>

            <StepsBar>
                {STEPS.map((s, index) => {
                    const Icon = s.icon;
                    const stepNumber = index + 1;
                    const active = stepNumber === step;
                    const done = stepNumber < step;
                    return (
                        <React.Fragment key={s.key}>
                            <StepBadge $active={active} $done={done}>
                                {done ? <Check size={13} strokeWidth={2.5} /> : <Icon size={13} strokeWidth={2.2} />}
                                <span>{s.label}</span>
                            </StepBadge>
                            {index !== STEPS.length - 1 && <StepConnector $done={done} />}
                        </React.Fragment>
                    );
                })}
            </StepsBar>

            {step === 1 && (
                <StepPanel>
                    <SectionTitle>
                        <SectionIcon><Users size={15} strokeWidth={2.2} /></SectionIcon>
                        <Text type='bold'>Quem vai receber</Text>
                    </SectionTitle>

                    <RadioGroup>
                        {AUDIENCE_OPTIONS.map(opt => (
                            <Radio
                                key={opt.name}
                                icon={opt.icon}
                                label={opt.label}
                                description={opt.hint}
                                accentColor={ACCENT_COLOR}
                                selected={opt.name === audienceType}
                                onSelect={() => { setAudienceType(opt.name); setEventId(''); setSelectedEventIds([]); }}
                            />
                        ))}
                    </RadioGroup>

                    {audienceType === 'event_participants' && (
                        <EventSelect value={eventId} onChange={e => setEventId(e.target.value)}>
                            <option value=''>Selecione um evento...</option>
                            {events.map(ev => (
                                <option key={ev.id} value={ev.id}>{ev.name}</option>
                            ))}
                        </EventSelect>
                    )}

                    {audienceType === 'event_combination' && (
                        <EventCheckboxList>
                            {events.length === 0 && (
                                <Text name='small' color={colors.nightLight}>Nenhum evento disponível.</Text>
                            )}
                            {events.map(ev => (
                                <Checkbox
                                    key={ev.id}
                                    label={ev.name}
                                    textSize='small'
                                    accentColor={ACCENT_COLOR}
                                    selected={selectedEventIds.includes(ev.id)}
                                    onSelect={() => toggleCombinationEvent(ev.id)}
                                />
                            ))}
                        </EventCheckboxList>
                    )}

                    <AudiencePreview>
                        {audienceType === 'event_participants' && !eventId ? (
                            <Text name='small' color={colors.nightLight}>
                                Selecione um evento para ver quantas pessoas vão receber.
                            </Text>
                        ) : audienceType === 'event_combination' && selectedEventIds.length < MIN_COMBINATION_EVENTS ? (
                            <Text name='small' color={colors.nightLight}>
                                Selecione pelo menos {MIN_COMBINATION_EVENTS} eventos para combinar o público.
                            </Text>
                        ) : audienceCountLoading ? (
                            <FullyCentered><Loader size='small' /></FullyCentered>
                        ) : audienceCountError ? (
                            <Text name='small' color={colors.danger}>{audienceCountError}</Text>
                        ) : (
                            <>
                                <AudienceCountBadge>
                                    <Users size={14} strokeWidth={2.4} />
                                    {audienceCount} destinatário{audienceCount !== 1 ? 's' : ''}
                                </AudienceCountBadge>
                                {audienceCount === 0 && (
                                    <Text name='small' color={colors.nightLight} marginTop='8px'>
                                        Ninguém se encaixa nesse público ainda (ou já pediu pra não receber e-mails seus).
                                    </Text>
                                )}
                            </>
                        )}
                    </AudiencePreview>
                </StepPanel>
            )}

            {step === 2 && (
                <StepPanel>
                    <SectionTitle>
                        <SectionIcon><Mail size={15} strokeWidth={2.2} /></SectionIcon>
                        <Text type='bold'>Conteúdo do e-mail</Text>
                    </SectionTitle>

                    <ContentGrid>
                        <ContentEditorCol>
                            <FieldLabel>Assunto</FieldLabel>
                            <SubjectInput
                                ref={subjectRef}
                                type='text'
                                maxLength={255}
                                placeholder='Ex: Novidades pro nosso próximo evento'
                                value={subject}
                                onChange={e => setSubject(e.target.value)}
                            />

                            {availableVariables.length > 0 && (
                                <VariablesRow>
                                    <VariablesLabel>
                                        <Sparkles size={12} strokeWidth={2.2} />
                                        Inserir variável:
                                    </VariablesLabel>
                                    {availableVariables.map(v => (
                                        <VariableChip
                                            key={v.key}
                                            type='button'
                                            onClick={() => insertVariableIntoSubject(`{{${v.key}}}`)}
                                        >
                                            {v.label}
                                        </VariableChip>
                                    ))}
                                </VariablesRow>
                            )}

                            <FieldLabel>Mensagem</FieldLabel>

                            {availableVariables.length > 0 && (
                                <VariablesRow>
                                    <VariablesLabel>
                                        <Sparkles size={12} strokeWidth={2.2} />
                                        Inserir variável:
                                    </VariablesLabel>
                                    {availableVariables.map(v => (
                                        <VariableChip
                                            key={v.key}
                                            type='button'
                                            onClick={() => bodyEditorRef.current?.insertText(`{{${v.key}}}`)}
                                        >
                                            {v.label}
                                        </VariableChip>
                                    ))}
                                </VariablesRow>
                            )}

                            <RichTextEditor
                                ref={bodyEditorRef}
                                allowImages={false}
                                onChange={setBody}
                                onChangePureText={setBodyText}
                            />

                            <VariablesHint>
                                Cada variável é substituída automaticamente pelo dado de cada destinatário no envio. Se não houver valor pra alguém, ela fica em branco.
                            </VariablesHint>
                        </ContentEditorCol>

                        <ContentPreviewCol>
                            <PreviewColLabel>
                                <Eye size={13} strokeWidth={2.2} />
                                Como vai chegar
                            </PreviewColLabel>
                            <EmailPreviewFrame subject={subject} bodyHtml={body} />
                        </ContentPreviewCol>
                    </ContentGrid>
                </StepPanel>
            )}

            {step === 3 && (
                <StepPanel>
                    <SectionTitle>
                        <SectionIcon><Send size={15} strokeWidth={2.2} /></SectionIcon>
                        <Text type='bold'>Revisão e envio</Text>
                    </SectionTitle>

                    <RadioGroup>
                        <Radio
                            label='Enviar agora'
                            icon={Send}
                            accentColor={ACCENT_COLOR}
                            selected={sendMode === 'now'}
                            onSelect={() => setSendMode('now')}
                        />
                        <Radio
                            label='Agendar para depois'
                            icon={CalendarClock}
                            accentColor={ACCENT_COLOR}
                            selected={sendMode === 'schedule'}
                            onSelect={() => setSendMode('schedule')}
                        />
                    </RadioGroup>

                    {sendMode === 'schedule' && (
                        <ScheduleInput
                            type='datetime-local'
                            min={minScheduledAt}
                            value={scheduledAt}
                            onChange={e => setScheduledAt(e.target.value)}
                        />
                    )}

                    <SummaryRow>
                        <SummaryChip>
                            <Users size={13} strokeWidth={2.3} />
                            {audienceType === 'customers' && 'Todos os clientes'}
                            {audienceType === 'event_participants' && (selectedEvent?.name || 'Evento selecionado')}
                            {audienceType === 'event_combination' && `${selectedCombinationEvents.length} eventos combinados`}
                        </SummaryChip>
                        <SummaryChip $accent>
                            {audienceCount ?? '—'} destinatário{audienceCount !== 1 ? 's' : ''}
                        </SummaryChip>
                    </SummaryRow>

                    <FieldLabel>Prévia da mensagem</FieldLabel>
                    <EmailPreviewFrame subject={subject} bodyHtml={body} />

                    {/\{\{\s*\w+\s*\}\}/.test(subject + body) && (
                        <Text name='mini' color={colors.nightLight} marginTop='8px'>
                            As variáveis destacadas (ex: {'{{nome}}'}) aparecem assim na prévia — cada destinatário recebe com os próprios dados.
                        </Text>
                    )}

                    {submitError && (
                        <FilterWarning>
                            <AlertTriangle size={15} strokeWidth={2.2} aria-hidden='true' />
                            <span>{submitError}</span>
                        </FilterWarning>
                    )}
                </StepPanel>
            )}

            <Actions>
                {step > 1 ? (
                    <Button category='outline' variant={submitting ? 'disabled' : 'primary'} onClick={goBack}>
                        <ChevronLeft size={15} />
                        Voltar
                    </Button>
                ) : <span />}

                {step < STEPS.length ? (
                    <Button
                        variant={(step === 1 ? !canAdvanceFromAudience : !canAdvanceFromContent) ? 'disabled' : 'featured'}
                        onClick={goNext}
                    >
                        Próximo
                        <ChevronRight size={15} />
                    </Button>
                ) : !submitting ? (
                    <Button variant={canSubmit ? 'featured' : 'disabled'} onClick={canSubmit ? handleSubmit : undefined}>
                        {sendMode === 'schedule' ? <Clock size={15} color={colors.neutralLight} /> : <Send size={15} color={colors.neutralLight} />}
                        <Text color={colors.neutralLight} paddingBottom={0} marginLeft='6px'>
                            {sendMode === 'schedule' ? 'Agendar campanha' : 'Enviar campanha'}
                        </Text>
                    </Button>
                ) : (
                    <FullyCentered><Loader size='small' /></FullyCentered>
                )}
            </Actions>
        </Wrapper>
    );
}

/* ─── Layout ─── */

const Wrapper = styled.div`
    padding: 20px 22px 22px;
`;

const ComposerHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const ComposerHeaderText = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ComposerIcon = styled.span`
    display: inline-flex;
    color: ${ACCENT_COLOR};
`;

const StepsBar = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 18px;
`;

const StepBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    color: ${({ $active, $done }) => ($active || $done) ? ACCENT_COLOR : colors.nightLight};
    background: ${({ $active }) => $active ? ACCENT_SOFT : 'transparent'};
`;

const StepConnector = styled.div`
    flex: 1;
    height: 1px;
    min-width: 12px;
    margin: 0 2px;
    background: ${({ $done }) => $done ? ACCENT_COLOR : 'rgba(16, 24, 40, 0.12)'};
`;

const StepPanel = styled.div`
    min-height: 260px;
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SectionIcon = styled.span`
    display: inline-flex;
    flex-shrink: 0;
    color: ${ACCENT_COLOR};
`;

const EventSelect = styled.select`
    margin-top: 12px;
    padding: 9px 12px;
    border-radius: 8px;
    border: 1px solid ${colors.neutralDark};
    font-size: 13px;
    color: ${colors.night};
    background: #fff;
    width: 100%;
    max-width: 360px;
`;

const ScheduleInput = styled.input`
    margin-top: 10px;
    padding: 9px 12px;
    border-radius: 8px;
    border: 1px solid ${colors.neutralDark};
    font-size: 13px;
    color: ${colors.night};
    background: #fff;
    width: 100%;
    max-width: 260px;
`;

const EventCheckboxList = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 12px;
    padding: 10px 14px;
    max-height: 220px;
    overflow-y: auto;
    border-radius: 10px;
    background: ${colors.neutralBackground};
    border: 1px solid rgba(16, 24, 40, 0.06);
`;

const AudiencePreview = styled.div`
    margin-top: 16px;
    padding: 12px 14px;
    border-radius: 10px;
    background: ${colors.neutralBackground};
    border: 1px solid rgba(16, 24, 40, 0.06);
    min-height: 20px;
`;

const AudienceCountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    color: ${ACCENT_COLOR};
`;

const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 24px;
    margin-top: 12px;

    @media screen and (max-width: ${breakpoints.lg}) {
        grid-template-columns: 1fr;
    }
`;

const ContentEditorCol = styled.div`
    min-width: 0;
`;

const ContentPreviewCol = styled.div`
    min-width: 0;

    @media screen and (max-width: ${breakpoints.lg}) {
        margin-top: 8px;
    }
`;

const PreviewColLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 16px 0 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightLight};
`;

const FieldLabel = styled.p`
    margin: 16px 0 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightLight};
`;

const SubjectInput = styled.input`
    width: 100%;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid ${colors.neutralDark};
    font-size: 13px;
    color: ${colors.night};
`;

const VariablesRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
`;

const VariablesLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    margin-right: 2px;
`;

const VariableChip = styled.button`
    padding: 3px 9px;
    border-radius: 20px;
    border: 1px solid ${ACCENT_COLOR};
    background: ${ACCENT_SOFT};
    color: ${ACCENT_COLOR};
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: ${panelAccentAlpha(colors.secondaryPurple, '20')};
    }
`;

const VariablesHint = styled.p`
    margin: 10px 0 0;
    font-size: 11px;
    color: ${colors.nightLight};
    line-height: 1.4;
`;

const RadioGroup = styled.div`
    margin-top: 4px;
`;

const SummaryRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 16px 0;
`;

const SummaryChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 50px;
    font-size: 12px;
    font-weight: 600;
    background: ${({ $accent }) => $accent ? ACCENT_SOFT : colors.neutralBackground};
    color: ${({ $accent }) => $accent ? ACCENT_COLOR : colors.nightDark};
`;

const FilterWarning = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 16px;
    padding: 10px 12px;
    border-radius: 8px;
    background: rgba(231, 80, 90, 0.1);
    color: ${colors.dangerDark};
    font-size: 12px;
    line-height: 1.4;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 22px;
    padding-top: 16px;
    border-top: 1px solid rgba(16, 24, 40, 0.09);
`;
